import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { ReportList } from "components/reporting/ReportList";
import { NotificationInline } from "components/notifications";
import { Tabs, Tab, MenuItem, Icon } from "@blueprintjs/core";
import { Panel } from "components/panel";
import { ActivitiesList, InfoPanel } from 'components/vehicle';
import { ShowHide } from "components/layout";
import { HarshBrakingPanel } from "components/vehicle/HarshBrakingPanel";
import { ActivitiesGraph } from "components/vehicle/ActivitiesGraph";
import { AssignDriverModal } from "pages/vehicle/assign-driver-modal/AssignDriverModal";
import { UnassignDriverModal } from "pages/vehicle/unassign-driver-modal/UnassignDriverModal";
import { UserHasAccess } from "components/user-access";
import { AssignJourneyModal } from "pages/vehicle/assign-journey-modal";
import { UnassignJourneyModal } from "pages/vehicle/unassign-journey-modal";
import './ActivitiesTab.css';
import { DateToLocal } from "components/dates";
import { GetFilesButton } from "components/vehicle/GetFilesButton";
import { Comment } from "components/comments";

export function ActivitiesTab(
    {
        lastSearchPayload = {},
        isLoading = false,
        activitiesApiResponse,
        assetDetails,
        date,
        harshBrakingApiResponse,
        modalOpen,
        refreshTachoAndHistoryData,
        reloadCalendar,
        vehicleActivityTypes
    }
) {
    const summaryColor = "#008CFF";
    const harshBrakingColour = "#F0578A";

    const activitiesSummaryTabId = "SUMMARY";

    const vehicleUploadedDrivingNoCard = 2;
    const vehicleActivityFromDriverCard = 3;
    const assignedJourney = 4;
    const driverAssignedToVehicleData = 5;

    const assignDriversPermission = "Fleet:AssignWithoutCard";
    const updateCommentsPermission = "Fleet:Comments";
    const canAssignDrivers = UserHasAccess(assignDriversPermission);
    const canUpdateComments = UserHasAccess(updateCommentsPermission);

    const [reportDownloading, setReportDownloading] = useState(false);
    const [displayedActivities, setDisplayedActivities] = useState([]);
    const [undisplatedActivities, setUndisplayedActivities] = useState([]);
    const [cardIwPanelData, setCardIwPanelData] = useState([]);
    const [harshBrakingPanelData, setHarshBrakingPanelData] = useState(<></>);
    const [dataSelectTabs, setDataSelectTabs] = useState([]);
    const [currentActivityTab, setCurrentActivityTab] = useState("");
    //CardIw for assigning and unassigning driver and journey
    const [cardIwForAction, setCardIwForAction] = useState(null);
    const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
    const [showUnassignModal, setShowUnassignModal] = useState(false);
    const [showAssignJourneyModal, setShowAssignJourneyModal] = useState(false);
    const [showUnassignJourneyModal, setShowUnassignJourneyModal] = useState(false);
    const [highlightedActivity, setHighlightedActivity] = useState(null);
    const [highlightedHarshBraking, setHighlightedHarshBraking] = useState(null);
    const currentDate = useRef();

    function onReportDownloading(isDownloading) {
        setReportDownloading(isDownloading);
    }

    function getColourFromType(vehicleActivityType) {
        if (!vehicleActivityType) {
            return summaryColor;
        }
        return vehicleActivityTypes.find((t) => t.id === vehicleActivityType).colours;
    }

    function getPanelTitleFromType(vehicleActivityType, journeyName) {
        if (!vehicleActivityType) {
            return "Summary";
        }
        let title = vehicleActivityTypes.find((t) => t.id === vehicleActivityType)?.name ?? 'Unknown';
        if (journeyName && journeyName.length > 0) {
            title = `${title} - ${journeyName}`;
        }
        return title;
    }

    function onHbRowClick(harshBraking) {
        setHighlightedHarshBraking(harshBraking);
        setHighlightedActivity(null);
    }

    function formatHarshBraking(data) {
        if (data.length !== 0)
            return <HarshBrakingPanel items={data} leftColor={harshBrakingColour} onHbRowClick={onHbRowClick} highlightedHarshBraking={highlightedHarshBraking} />;
        else
            return <></>;
    }

    function filterActivitiesByDate(activities, dateValue) {
        return activities.filter((a) => moment(a.startDate).add("minuite", a.duration).startOf("day").isSame(moment(dateValue).startOf("day")));
    }

    function setDisplayedActivitiesData() {
        if (currentDate && currentDate.current && currentDate.current !== date) {
            setCurrentActivityTab(activitiesSummaryTabId);
            setHighlightedActivity(null);
            setHighlightedHarshBraking(null);
        }

        currentDate.current = date;

        setUndisplayedActivities([]);
        if (String(currentActivityTab).startsWith("CardIw")) {
            if (!activitiesApiResponse || !activitiesApiResponse.cardIws) {
                setDisplayedActivities([]);
            }
            else {
                let cardIw = activitiesApiResponse.cardIws.find((c) => c.cardIwId === parseInt(currentActivityTab.replace("CardIw", "")));
                setDisplayedActivities(() => {
                    if (cardIw) {
                        const activities = filterActivitiesByDate(cardIw.activities, date);
                        return activities.map((a) => {
                            return Object.assign(a, { bgColor: getColourFromType(cardIw.vehicleActivityType) });
                        });
                    }
                    else {
                        return [];
                    }
                });
            }

            //set 'undisplayed' activities, this is the grey data for the graph:
            setUndisplayedActivities(() => {
                let response = [];
                if (!activitiesApiResponse || !activitiesApiResponse.cardIws) {
                    return response;
                }
                let currentCardIw = activitiesApiResponse.cardIws.find((c) => c.cardIwId === parseInt(currentActivityTab.replace("CardIw", "")));
                let subsequentActivities = [];
                if (currentCardIw) {
                    subsequentActivities = activitiesApiResponse.cardIws.filter((c) => c.cardIwId !== currentCardIw.cardIwId && moment(c.startDate).isAfter(currentCardIw.startDate));
                }
                if (subsequentActivities && subsequentActivities.length > 0) {
                    let activities = subsequentActivities.map((a) => {
                        return a.activities;
                    });
                    response = activities.flat();
                }
                //Add any current activities that were stripped out because they went over midnight:
                if (currentCardIw) {
                    let tomorrowsActivities = filterActivitiesByDate(currentCardIw.activities, moment(date).add(1, 'days').format("YYYY-MM-DD"));
                    response.push(...tomorrowsActivities);
                }
                return response;
            });
        }
        else {
            //Summary tab:
            setDisplayedActivities(() => {
                if (activitiesApiResponse && activitiesApiResponse.cardIws && activitiesApiResponse.cardIws.length > 0) {
                    return getAllActivitiesFromCardIws(activitiesApiResponse.cardIws);
                }
                else {
                    return [];
                }
            });
        }

        //Tabs:
        setCardIwTabs(activitiesApiResponse.cardIws, activitiesApiResponse.summary);
        //Panels:
        setCardIwPanels(activitiesApiResponse.cardIws, activitiesApiResponse.summary);
    }

    function getAllActivitiesFromCardIws(cardIws) {
        return cardIws.map((c) => {
            const activities = filterActivitiesByDate(c.activities, date);
            return activities.map((a) => {
                return Object.assign(a, { bgColor: getColourFromType(c.vehicleActivityType) });
            });
        }).flat();
    }

    function areSameDate(date1, date2) {
        return moment(date1).isSame(date2, 'day');
    }

    function setCardIwTabs(cardIws, summary) {
        let dataSelectTabData = [];
        if (summary) {
            //Summary tab
            dataSelectTabData.push({ uid: activitiesSummaryTabId, color: summaryColor, title: `${moment(date).format("DD MMM YYYY")} Summary`, selected: currentActivityTab === activitiesSummaryTabId, key: activitiesSummaryTabId, comment: null });
        }
        if (cardIws) {
            cardIws.map((c) => {
                dataSelectTabData.push({ uid: `CardIw${c.cardIwId}`, color: getColourFromType(c.vehicleActivityType), title: <><DateToLocal format="DD MMM YYYY HH:mm">{c.startDate}</DateToLocal> - <DateToLocal format={areSameDate(c.startDate, c.endDate) ? "HH:mm" : "DD MMM YYYY HH:mm"}>{c.endDate}</DateToLocal></>, selected: currentActivityTab === `CardIw${c.cardIwId}`, key: c.cardIwId, comment: c.comment });
            });
        }
        setDataSelectTabs(() => dataSelectTabData.map((d) => {
            return (
                <Tab className="summary-tab-item" id={d.uid} key={d.uid} >
                    <Panel color={d.color} isTab={true} selected={d.selected}>
                        {d.title} {d.comment !== null ? <Icon icon="comment" size={12} /> : ""}
                    </Panel>
                </Tab>
            );
        }));
    }

    useEffect(() => {
        if (!modalOpen) {
            setCurrentActivityTab("");
        } else {
            setCurrentActivityTab(activitiesSummaryTabId);
        }
    }, [modalOpen]);

    useEffect(() => {
        if (assetDetails && date && activitiesApiResponse && activitiesApiResponse.cardIws && currentActivityTab) {
            setDisplayedActivitiesData();
        }
    }, [activitiesApiResponse, assetDetails, date, currentActivityTab]);


    useEffect(() => {
        if (harshBrakingApiResponse && harshBrakingApiResponse.length !== 0) {
            let hbToUse = harshBrakingApiResponse;
            if (currentActivityTab && currentActivityTab !== activitiesSummaryTabId) {
                let cardIw = activitiesApiResponse.cardIws.find((c) => c.cardIwId === parseInt(currentActivityTab.replace("CardIw", "")));
                if (cardIw) {
                    hbToUse = harshBrakingApiResponse.filter((h) => moment(h.startDate).isBetween(cardIw.startDate, cardIw.endDate));
                }
            }
            setHarshBrakingPanelData(formatHarshBraking(hbToUse));
        }
        else {
            setHarshBrakingPanelData(<></>);
        }
    }, [harshBrakingApiResponse, highlightedHarshBraking, currentActivityTab]);

    function handleCardIwCommentUpdate() {
        refreshTachoAndHistoryData();
    }

    function setCardIwPanels(cardIws, summary) {
        let cardIwTabs = [];
        if (summary) {
            let startOdo = null;
            let endOdo = null;
            if (cardIws.length > 0) {
                startOdo = cardIws[0].summary.startOdo;
                endOdo = cardIws[cardIws.length - 1].summary.endOdo;
            }
            let driverNames = cardIws.filter((d) => d.driverName && d.driverName.length > 0).map((d) => d.driverName);

            // Collect all comments from cardIws for summary view (only those with actual comments)
            const summaryComments = cardIws
                .filter((c) => c.comment && c.comment.trim().length > 0)
                .map((c, index) => {
                    // Create a label for the comment based on the activity
                    const activityLabel = getPanelTitleFromType(c.vehicleActivityType, c.journeyName) + (c.driverName ? ` - ${c.driverName}` : '');

                    return (
                        <div key={`summary-comment-${c.cardIwId}`}>
                            <Comment
                                onSave={() => { }}
                                onDelete={() => { }}
                                value={c.comment}
                                id={`summary-comment-panel-${c.cardIwId}`}
                                isEditibleInitially={false}
                                className="info-panel-comment"
                                headingText={activityLabel}
                                headerText={index === 0 ? "Comments" : null}
                                showActions={false}
                            />
                        </div>
                    );
                });

            cardIwTabs.push(
                {
                    "id": activitiesSummaryTabId,
                    "content": (
                        <InfoPanel
                            drive={summary.drive}
                            work={summary.work}
                            rest={summary.rest}
                            poa={summary.poa}
                            leftColor={summaryColor}
                            headingText={getPanelTitleFromType(null)}
                            isSummary={true}
                            startDate={date}
                            endDate={null}
                            startOdo={startOdo}
                            endOdo={endOdo}
                            allowAddComment={false}
                            driverName={driverNames.join(" - ")}
                        >
                            {canUpdateComments && summaryComments.length > 0 && (
                                <div className="summary-comments-section">
                                    {summaryComments}
                                </div>
                            )}
                        </InfoPanel>)
                }
            );
        }
        if (cardIws) {
            cardIwTabs.push(
                ...cardIws.map((c) => {
                    return (
                        {
                            "id": `CardIw${c.cardIwId}`,
                            "content": (
                                <InfoPanel
                                    drive={c.summary.drive}
                                    work={c.summary.work}
                                    rest={c.summary.rest}
                                    poa={c.summary.poa}
                                    startOdo={c.summary.startOdo}
                                    endOdo={c.summary.endOdo}
                                    driverName={c.driverName}
                                    leftColor={getColourFromType(c.vehicleActivityType)}
                                    headingText={getPanelTitleFromType(c.vehicleActivityType, c.journeyName)}
                                    isSummary={false}
                                    startDate={c.startDate}
                                    endDate={c.endDate}
                                    showBurger={true}
                                    cardIwId={c.cardIwId}
                                    initialComment={c.comment}
                                    onCommentChange={handleCardIwCommentUpdate}
                                    burgerItems={getActionsFromActivityType(c.vehicleActivityType, c.cardIwId)}
                                    allowAddComment={canUpdateComments && c.vehicleActivityType !== vehicleActivityFromDriverCard}
                                />
                            )
                        }
                    );
                })
            );
        }
        setCardIwPanelData(cardIwTabs);
    }

    function onAssignDriverModalClose(driverId) {
        setShowAssignDriverModal(false);

        if (driverId) {
            refreshTachoAndHistoryData();
        }
        setCardIwForAction(null);
    }

    function onShowAssignDriverModalOpen(cardIwId) {
        setShowAssignDriverModal(true);
        changeCardIw(cardIwId);
    }

    function onUnassignDriverModalOpen(cardIwId) {
        setShowUnassignModal(true);
        changeCardIw(cardIwId);
    }

    function onUnassignDriverModalClose(unassigned) {
        setShowUnassignModal(false);
        if (unassigned) {
            refreshTachoAndHistoryData();
        }
        setCardIwForAction(null);
    }

    function onAssignJourneyChange(show, cardIwId) {
        setShowAssignJourneyModal(show);
        changeCardIw(cardIwId);
    }

    function assignJourneyClose() {
        refreshTachoAndHistoryData();
        setShowAssignJourneyModal(false)
    }

    function onUnassignJourneyModalChange(show, cardIwId) {
        setShowUnassignJourneyModal(show);
        changeCardIw(cardIwId);
    }

    function unassignJourneyClose() {
        refreshTachoAndHistoryData();
        setShowUnassignJourneyModal(false)
    }

    function changeCardIw(cardIwId) {
        let cardIwObject = activitiesApiResponse.cardIws.find((c) => c.cardIwId === cardIwId);
        setCardIwForAction(cardIwObject);
    }

    function getActionsFromActivityType(vehicleActivityType, cardIwId) {
        let actions = [];

        if (vehicleActivityType === vehicleUploadedDrivingNoCard && canAssignDrivers) {
            actions.push(<MenuItem onClick={() => onShowAssignDriverModalOpen(cardIwId)} text="Assign Driver" key={`${cardIwId}-mi-assign-driver`} />);
            actions.push(<MenuItem onClick={() => onAssignJourneyChange(true, cardIwId)} text="Assign Journey" key={"mi-assign-journey"} />);
        }
        if (vehicleActivityType === driverAssignedToVehicleData && canAssignDrivers) {
            actions.push(<MenuItem onClick={() => onUnassignDriverModalOpen(cardIwId)} text="Unassign Driver" key={`${cardIwId}-mi-unassign-driver`} />);
        }
        if (vehicleActivityType === assignedJourney && canAssignDrivers) {
            actions.push(<MenuItem onClick={() => onUnassignJourneyModalChange(true, cardIwId)} text="Unassign Journey" key={`${cardIwId}-mi-unassign-journey`} />);
        }

        return actions;
    }

    function onActivityTabChange(id) {
        setHighlightedActivity(null);
        setHighlightedHarshBraking(null);
        setCurrentActivityTab(id);
    }

    function handleHighlightActivity(activity) {
        if (activity && activity.activityId !== highlightedActivity?.activityId) {
            setHighlightedActivity(activity);
            setHighlightedHarshBraking(null);
        }
    }

    function handleSave() {
        if (reloadCalendar) {
            reloadCalendar();
        }
        onActivityTabChange(activitiesSummaryTabId);
    }

    return (
        <div className="asset-activities-tab-container">
            <AssignDriverModal
                showModal={showAssignDriverModal}
                onCloseModal={onAssignDriverModalClose}
                assetDetails={assetDetails}
                date={date}
                cardIw={cardIwForAction}
                onSave={handleSave}
            />
            <AssignJourneyModal
                showModal={showAssignJourneyModal}
                onCloseModal={assignJourneyClose}
                date={date}
                assetDetails={assetDetails}
                cardIwData={cardIwForAction}
                onSave={handleSave}
            />
            <UnassignDriverModal
                isOpen={showUnassignModal}
                onClose={onUnassignDriverModalClose}
                cardIw={cardIwForAction}
                assetId={assetDetails.assetId.toString()}
                onSave={handleSave}
            />
            <UnassignJourneyModal
                isOpen={showUnassignJourneyModal}
                onClose={unassignJourneyClose}
                assetId={assetDetails.assetId.toString()}
                cardIw={cardIwForAction}
                onSave={handleSave}
            />
            <div>
                <div className="inline-items activities-tab-buttons">
                    <ReportList lastSearchPayload={{ filters: [{ "key": "assetId", "value": assetDetails.assetId }], "startDate": date, "endDate": date }} pageName="DayDetail-Activities" popoverUsePortal={false} onReportDownloadingChange={onReportDownloading} disabled={isLoading} />
                    <GetFilesButton uploadsList={activitiesApiResponse.uploads} disabled={isLoading} />
                </div>
                <NotificationInline intent="success" text="Your report has been queued, and will download as soon as it's ready." show={reportDownloading} />
            </div>
            <div id="graph">
                <ActivitiesGraph
                    activitiesList={displayedActivities}
                    undisplayedActivitiesList={undisplatedActivities}
                    isLoading={isLoading}
                    isSummary={activitiesSummaryTabId === currentActivityTab}
                    baseDate={date}
                    highlightedActivity={highlightedActivity}
                    highlightedHarshBraking={highlightedHarshBraking}
                    onActivitySelected={handleHighlightActivity}
                    harshBraking={harshBrakingApiResponse}
                />
            </div>
            <div>
                <ShowHide evaluator={!isLoading}
                    show={
                        <Tabs className="summary-tab-list" onChange={(p) => onActivityTabChange(p)} animate={false}>
                            {dataSelectTabs}
                        </Tabs>
                    }
                    hide={<Tabs className="summary-tab-list"><Tab className="summary-tab-item"><Panel className="summary-tab-item-disabled bp6-skeleton" color="#333333" >Loading...</Panel></Tab></Tabs>} />
            </div>
            <div className="asset-activities-tab" id="content">
                <div id="listing">
                    <ActivitiesList activitiesList={displayedActivities} isLoading={isLoading} onActivitySelected={handleHighlightActivity} highlightedActivity={highlightedActivity} id="activities-tab-activities-list" />
                </div>
                <div id="info-panels">
                    <ShowHide evaluator={!isLoading}
                        hide={
                            <InfoPanel disabled={true} startDate={date} headingText="" />
                        } show={
                            <>
                                <div className="info-panel-margin-bottom">
                                    {cardIwPanelData.find((c) => c.id === currentActivityTab)?.content}
                                </div>
                                {harshBrakingPanelData}
                            </>
                        } />
                </div>
            </div>
        </div>
    );
}

ActivitiesTab.propTypes = {
    assetDetails: PropTypes.object.isRequired,
    date: PropTypes.string,
    lastSearchPayload: PropTypes.object,
    activitiesApiResponse: PropTypes.object,
    harshBrakingApiResponse: PropTypes.array,
    isLoading: PropTypes.bool,
    vehicleActivityTypes: PropTypes.array.isRequired,
    refreshTachoAndHistoryData: PropTypes.func.isRequired,
    modalOpen: PropTypes.bool,
    reloadCalendar: PropTypes.func
};